import * as THREE from 'three';

const THUMBNAIL_SIZE=512;
const THUMBNAIL_QUALITY=.86;
const TYPE_COLORS={
  vehicle:['#24304f','#39d9ee'],
  wheel:['#222436','#d9dde2'],
  engine_sound:['#231d3c','#b176ff'],
  cannon:['#312238','#ffcf43'],
  projectile:['#1c2940','#5eefff'],
  nitro:['#182f3b','#39d9ee'],
  explosion:['#3a201e','#ff8a35'],
  tombstone:['#29273a','#bdc3d6'],
};

function safeFilePart(value,fallback='item'){
  return String(value||fallback)
    .normalize('NFD').replace(/[\u0300-\u036f]/g,'')
    .replace(/[^a-zA-Z0-9._-]+/g,'-')
    .replace(/^-+|-+$/g,'')
    .toLowerCase()||fallback;
}

function canvasBlob(canvas,type,quality){
  return new Promise((resolve)=>canvas.toBlob(resolve,type,quality));
}

async function thumbnailFile(canvas,id,type){
  let blob=await canvasBlob(canvas,'image/webp',THUMBNAIL_QUALITY);
  let extension=blob?.type==='image/webp'?'webp':'png';
  if(!blob){
    blob=await canvasBlob(canvas,'image/png');
    extension='png';
  }
  if(!blob)throw new Error('El navegador no pudo generar la miniatura.');
  return new File(
    [blob],
    `${safeFilePart(id)}-${safeFilePart(type,'catalog')}.${extension}`,
    {type:blob.type,lastModified:Date.now()},
  );
}

function paintThumbnail(sourceCanvas,type){
  if(!sourceCanvas?.width||!sourceCanvas?.height){
    throw new Error('La vista previa todavía no está lista para generar su miniatura.');
  }
  const canvas=document.createElement('canvas');
  canvas.width=THUMBNAIL_SIZE;
  canvas.height=THUMBNAIL_SIZE;
  const context=canvas.getContext('2d',{alpha:false});
  const [background,accent]=TYPE_COLORS[type]||TYPE_COLORS.vehicle;
  const gradient=context.createRadialGradient(
    THUMBNAIL_SIZE*.43,
    THUMBNAIL_SIZE*.38,
    THUMBNAIL_SIZE*.04,
    THUMBNAIL_SIZE*.5,
    THUMBNAIL_SIZE*.5,
    THUMBNAIL_SIZE*.72,
  );
  gradient.addColorStop(0,accent);
  gradient.addColorStop(.38,background);
  gradient.addColorStop(1,'#111020');
  context.fillStyle=gradient;
  context.fillRect(0,0,THUMBNAIL_SIZE,THUMBNAIL_SIZE);
  context.drawImage(sourceCanvas,0,0,THUMBNAIL_SIZE,THUMBNAIL_SIZE);

  const vignette=context.createRadialGradient(
    THUMBNAIL_SIZE*.5,
    THUMBNAIL_SIZE*.46,
    THUMBNAIL_SIZE*.28,
    THUMBNAIL_SIZE*.5,
    THUMBNAIL_SIZE*.5,
    THUMBNAIL_SIZE*.7,
  );
  vignette.addColorStop(0,'rgba(5,4,14,0)');
  vignette.addColorStop(1,'rgba(5,4,14,.52)');
  context.fillStyle=vignette;
  context.fillRect(0,0,THUMBNAIL_SIZE,THUMBNAIL_SIZE);

  context.strokeStyle='rgba(255,255,255,.12)';
  context.lineWidth=4;
  context.strokeRect(2,2,THUMBNAIL_SIZE-4,THUMBNAIL_SIZE-4);
  return canvas;
}

function hiddenForThumbnail(object){
  const name=String(object.name||'').toLowerCase();
  return object.type==='GridHelper'
    ||name.includes('calibration_guides')
    ||name.includes('vehicle_comparison')
    ||name.includes('gameplay_collider')
    ||name.includes('vehicle_forward')
    ||name.includes('socket_')
    ||name.includes('height_');
}

export async function createEditorThumbnailFile({
  editor,
  id,
  type,
  cameraPosition,
  target,
  rotationObject,
  rotationY,
  frame,
  prepare,
}){
  if(!editor?.renderer||!editor?.scene||!editor?.camera||!editor?.canvas){
    throw new Error('El editor no tiene una vista 3D disponible para la miniatura.');
  }

  const rendererSize=editor.renderer.getSize(new THREE.Vector2());
  const cameraPositionBefore=editor.camera.position.clone();
  const cameraAspectBefore=editor.camera.aspect;
  const targetBefore=editor.controls?.target?.clone?.()||null;
  const rotationBefore=rotationObject?.rotation?.clone?.()||null;
  const hidden=[];
  editor.scene.traverse((object)=>{
    if(!object.visible||!hiddenForThumbnail(object))return;
    hidden.push(object);
    object.visible=false;
  });

  try{
    editor.renderer.setSize(THUMBNAIL_SIZE,THUMBNAIL_SIZE,false);
    editor.camera.aspect=1;
    editor.camera.updateProjectionMatrix();
    if(typeof frame==='function')frame();
    else{
      if(cameraPosition)editor.camera.position.fromArray(cameraPosition);
      if(target&&editor.controls?.target)editor.controls.target.fromArray(target);
      editor.controls?.update?.();
    }
    if(rotationObject&&Number.isFinite(rotationY))rotationObject.rotation.y=rotationY;
    prepare?.();
    editor.renderer.render(editor.scene,editor.camera);
    return await thumbnailFile(paintThumbnail(editor.canvas,type),id,type);
  }finally{
    hidden.forEach((object)=>{object.visible=true;});
    if(rotationBefore)rotationObject.rotation.copy(rotationBefore);
    editor.camera.position.copy(cameraPositionBefore);
    editor.camera.aspect=cameraAspectBefore;
    editor.camera.updateProjectionMatrix();
    if(targetBefore&&editor.controls?.target)editor.controls.target.copy(targetBefore);
    editor.controls?.update?.();
    editor.renderer.setSize(rendererSize.x,rendererSize.y,false);
    editor.renderer.render(editor.scene,editor.camera);
  }
}

function waveValue(wave,phase){
  if(wave==='sine')return Math.sin(phase);
  if(wave==='triangle')return 2/Math.PI*Math.asin(Math.sin(phase));
  if(wave==='square')return Math.sin(phase)>=0?1:-1;
  return 2*((phase/(Math.PI*2))-Math.floor(.5+phase/(Math.PI*2)));
}

export async function createEngineSoundThumbnailFile(design,id){
  const canvas=document.createElement('canvas');
  canvas.width=THUMBNAIL_SIZE;
  canvas.height=THUMBNAIL_SIZE;
  const context=canvas.getContext('2d',{alpha:false});
  const [background,accent]=TYPE_COLORS.engine_sound;
  const gradient=context.createLinearGradient(0,0,THUMBNAIL_SIZE,THUMBNAIL_SIZE);
  gradient.addColorStop(0,background);
  gradient.addColorStop(1,'#111020');
  context.fillStyle=gradient;
  context.fillRect(0,0,THUMBNAIL_SIZE,THUMBNAIL_SIZE);

  context.strokeStyle='rgba(255,255,255,.07)';
  context.lineWidth=2;
  for(let index=1;index<8;index+=1){
    const position=index/8*THUMBNAIL_SIZE;
    context.beginPath();
    context.moveTo(position,0);
    context.lineTo(position,THUMBNAIL_SIZE);
    context.moveTo(0,position);
    context.lineTo(THUMBNAIL_SIZE,position);
    context.stroke();
  }

  const tone=Math.max(20,Number(design?.baseTone)||58);
  const pitch=Math.max(.5,Number(design?.speedPitch)||7);
  const cycles=2.4+Math.min(5,tone/34+pitch/8);
  const amplitude=THUMBNAIL_SIZE*(.19+Math.min(.1,Number(design?.volume)||.035));
  context.shadowColor=accent;
  context.shadowBlur=24;
  context.strokeStyle=accent;
  context.lineWidth=12;
  context.lineJoin='round';
  context.beginPath();
  for(let x=30;x<=THUMBNAIL_SIZE-30;x+=2){
    const progress=(x-30)/(THUMBNAIL_SIZE-60);
    const envelope=.72+Math.sin(progress*Math.PI)*.28;
    const phase=progress*Math.PI*2*cycles;
    const y=THUMBNAIL_SIZE*.5-waveValue(design?.wave,phase)*amplitude*envelope;
    if(x===30)context.moveTo(x,y);
    else context.lineTo(x,y);
  }
  context.stroke();
  context.shadowBlur=0;

  context.fillStyle='rgba(255,255,255,.8)';
  context.font='900 68px sans-serif';
  context.textAlign='center';
  context.fillText('♫',THUMBNAIL_SIZE*.5,THUMBNAIL_SIZE*.79);
  return thumbnailFile(canvas,id,'engine_sound');
}
